import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import {
  AgoraRTCProvider,
  useRTCClient,
  useRemoteUsers,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteAudioTracks,
} from "agora-rtc-react";

/**
 * AgoraVideoPage.jsx — versão completa e refinada
 *
 * Objetivos de melhoria:
 *  - Arquitetura com Provider + Hooks do `agora-rtc-react`
 *  - Fluxo de "Lobby" (pré‑ingresso) com preview e teste de devices
 *  - Alternância dinâmica de microfone/câmera e troca de dispositivos sem reiniciar a call
 *  - Screen share com restauração automática da câmera (e nota para multi-client, se desejar evoluir)
 *  - Destaque de orador ativo (audio volume)
 *  - Indicadores simples de rede/bitrate
 *  - Tratamento de erros e mensagens de status
 *  - UI responsiva, single-file, pronta para colar no projeto
 *
 * Instalação:
 *   npm i agora-rtc-sdk-ng agora-rtc-react
 *
 * Produção:
 *   - Gere TOKEN no backend com expiração curta e RBAC.
 *   - Passe o token para este componente via props/context/fetch.
 */

// ========== Componente Raiz ==================================================
export default function AgoraVideoPage() {
  const [appId, setAppId] = useState("");
  const [channel, setChannel] = useState("sala-teste");
  const [token, setToken] = useState(""); // Em produção, obter do backend

  const client = useMemo(() => AgoraRTC.createClient({ mode: "rtc", codec: "vp8" }), []);

  return (
    <AgoraRTCProvider client={client}>
      <VideoCall appId={appId} setAppId={setAppId} channel={channel} setChannel={setChannel} token={token} setToken={setToken} />
    </AgoraRTCProvider>
  );
}

// ========== Subcomponentes ===================================================
function VideoCall({ appId, setAppId, channel, setChannel, token, setToken }) {
  const client = useRTCClient();

  // —— UI State
  const [step, setStep] = useState("lobby"); // lobby | in-call
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // —— Devices
  const [devices, setDevices] = useState({ mics: [], cams: [] });
  const [selectedMicId, setSelectedMicId] = useState("");
  const [selectedCamId, setSelectedCamId] = useState("");

  // —— Local Tracks
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(
    selectedMicId ? { microphoneId: selectedMicId, AEC: true, ANS: true, AGC: true } : { AEC: true, ANS: true, AGC: true }
  );
  const { localCameraTrack } = useLocalCameraTrack(
    selectedCamId ? { cameraId: selectedCamId, encoderConfig: "720p_3", facingMode: "user" } : { encoderConfig: "720p_3", facingMode: "user" }
  );

  // —— Publish/Unpublish automático quando em call ——
  const [canPublish, setCanPublish] = useState(false);
  usePublish(canPublish ? [localMicrophoneTrack, localCameraTrack] : []);

  // —— Remotos (vídeo) + tocar áudio remoto automaticamente ——
  const remoteUsers = useRemoteUsers();
  const { audioTracks: remoteAudioTracks } = useRemoteAudioTracks(remoteUsers);
  useEffect(() => {
    remoteAudioTracks.forEach((t) => {
      try { t.play(); } catch (e) { console.warn('Autoplay possivelmente bloqueado pelo navegador. Interaja com a página.', e); }
    });
  }, [remoteAudioTracks]);

  // —— Volume (destaque orador) ——
  const [speakingMap, setSpeakingMap] = useState({});
  useEffect(() => {
    let mounted = true;
    client.enableAudioVolumeIndicator();
    const handler = (vols) => {
      if (!mounted) return;
      const next = {};
      for (const v of vols) next[v.uid] = v.level > 5;
      setSpeakingMap(next);
    };
    client.on("volume-indicator", handler);
    return () => { mounted = false; client.off("volume-indicator", handler); };
  }, [client]);

  // —— Net quality / stats ——
  const [netQ, setNetQ] = useState({ uplink: 0, downlink: 0 });
  useEffect(() => {
    const onNet = (stats) => setNetQ({ uplink: stats.uplinkNetworkQuality, downlink: stats.downlinkNetworkQuality });
    client.on("network-quality", onNet);
    const id = setInterval(() => {
      const s = client.getRTCStats?.();
      if (s) setStatusMsg(`Bitrate disponível: ${Math.round(s.OutgoingAvailableBandwidth || 0)} kbps`);
    }, 3000);
    return () => { client.off("network-quality", onNet); clearInterval(id); };
  }, [client]);

  // —— Screen Share ——
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenTrackRef = useRef(null);
  const startScreenShare = useCallback(async () => {
    try {
      if (localCameraTrack) await client.unpublish(localCameraTrack);
      const screenTrack = await AgoraRTC.createScreenVideoTrack({ encoderConfig: "1080p_1", optimizationMode: "detail" }, "auto");
      screenTrackRef.current = screenTrack;
      await client.publish(screenTrack);
      setIsScreenSharing(true);
      screenTrack.on("track-ended", async () => stopScreenShare());
    } catch (e) {
      console.error(e); setErrorMsg("Falha no compartilhamento de tela.");
    }
  }, [client, localCameraTrack]);

  const stopScreenShare = useCallback(async () => {
    try {
      const screen = screenTrackRef.current;
      if (screen) {
        await client.unpublish(screen);
        screen.close();
        screenTrackRef.current = null;
      }
      if (localCameraTrack) await client.publish(localCameraTrack);
      setIsScreenSharing(false);
    } catch (e) { console.error(e); }
  }, [client, localCameraTrack]);

  // —— Lobby: carregar devices ——
  useEffect(() => {
    const loadDevices = async () => {
      try {
        const list = await AgoraRTC.getDevices();
        const mics = list.filter(d => d.kind === "audioinput");
        const cams = list.filter(d => d.kind === "videoinput");
        setDevices({ mics, cams });
        if (!selectedMicId && mics[0]) setSelectedMicId(mics[0].deviceId);
        if (!selectedCamId && cams[0]) setSelectedCamId(cams[0].deviceId);
      } catch (e) {
        console.error("Erro ao obter devices", e);
        setErrorMsg("Não foi possível listar os dispositivos");
      }
    };
    loadDevices();
  }, []);

  // —— Join/Leave ——
  const [allowJoin, setAllowJoin] = useState(false);
  const [joined, setJoined] = useState(false);

  useJoin(
    { appid: appId || undefined, channel: channel || undefined, token: token || null, uid: 0 },
    allowJoin,
  );

  useEffect(() => {
    const onConn = () => {
      const st = client.connectionState;
      if (st === "CONNECTED") { setJoined(true); setStep("in-call"); setCanPublish(true); setStatusMsg("Conectado."); }
      if (st === "DISCONNECTED") { setJoined(false); setStep("lobby"); setCanPublish(false); }
    };
    client.on("connection-state-change", onConn);
    return () => client.off("connection-state-change", onConn);
  }, [client]);

  const handleJoin = async () => {
    setErrorMsg("");
    if (!appId || !channel) { setErrorMsg("Informe App ID e Canal."); return; }
    try {
      setStatusMsg("Conectando…");
      setAllowJoin(true);
    } catch (e) {
      console.error(e); setErrorMsg(e?.message || String(e));
    }
  };

  const handleLeave = async () => {
    try {
      setStatusMsg("Saindo…");
      setCanPublish(false);
      if (screenTrackRef.current) {
        try { await client.unpublish(screenTrackRef.current); } catch {}
        try { screenTrackRef.current.close(); } catch {}
        screenTrackRef.current = null;
      }
      if (localCameraTrack) { try { await client.unpublish(localCameraTrack); } catch {} }
      if (localMicrophoneTrack) { try { await client.unpublish(localMicrophoneTrack); } catch {} }
      await client.leave();
      setAllowJoin(false);
      setStep("lobby");
      setStatusMsg("Desconectado.");
    } catch (e) {
      console.error(e); setErrorMsg("Falha ao sair");
    }
  };

  // —— Mute/Unmute ——
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  const toggleMic = useCallback(async () => {
    if (!localMicrophoneTrack) return;
    const next = !micOn; await localMicrophoneTrack.setEnabled(next); setMicOn(next);
  }, [localMicrophoneTrack, micOn]);

  const toggleCam = useCallback(async () => {
    if (isScreenSharing) return; // evita conflito com screen share
    if (!localCameraTrack) return;
    const next = !camOn; await localCameraTrack.setEnabled(next); setCamOn(next);
  }, [localCameraTrack, camOn, isScreenSharing]);

  // —— Preview: alterna track local exibida
  const previewTrack = isScreenSharing ? screenTrackRef.current : localCameraTrack;

  return (
    <div style={{ maxWidth: 1200, margin: "24px auto", padding: 16 }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Videochamada (Agora)</h1>
      <p style={{ color: "#555", marginBottom: 16 }}>Fluxo com Lobby, hooks do SDK React e recursos avançados.</p>

      <div style={{ display: "grid", gridTemplateColumns: step === "in-call" ? "1.5fr 1fr" : "1fr", gap: 16 }}>
        {/* Painel esquerdo */}
        <div>
          <div style={{ marginBottom: 12 }}>
            <VideoCard
              track={previewTrack}
              label={joined ? `Você (uid ${client.uid ?? "local"})` : "Pré‑visualização"}
              speaking={speakingMap[client.uid]}
            />
          </div>
          {step === "in-call" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
              {remoteUsers.map(u => (
                <VideoCard key={u.uid} track={u.videoTrack} label={`Remoto (uid ${u.uid})`} speaking={speakingMap[u.uid]} />
              ))}
            </div>
          )}
        </div>

        {/* Painel direito */}
        <div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 16, boxShadow: "0 10px 24px rgba(0,0,0,0.06)" }}>
            {step === "lobby" && (
              <LobbyControls
                appId={appId} setAppId={setAppId}
                channel={channel} setChannel={setChannel}
                token={token} setToken={setToken}
                devices={devices}
                selectedMicId={selectedMicId} setSelectedMicId={setSelectedMicId}
                selectedCamId={selectedCamId} setSelectedCamId={setSelectedCamId}
                onJoin={handleJoin}
              />
            )}

            {step === "in-call" && (
              <InCallControls
                micOn={micOn} camOn={camOn}
                toggleMic={toggleMic} toggleCam={toggleCam}
                isScreenSharing={isScreenSharing}
                startScreenShare={startScreenShare}
                stopScreenShare={stopScreenShare}
                onLeave={handleLeave}
                netQ={netQ}
                statusMsg={statusMsg}
              />
            )}

            {errorMsg && (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 12, background: "#fee2e2", color: "#991b1b" }}>{errorMsg}</div>
            )}
          </div>

          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>
            <p>
              <b>Dica:</b> Para câmera e tela simultâneas, use um <b>segundo client</b> dedicado ao screen share.
              Em produção, gere o token no backend com expiração curta.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// —— Cartão de vídeo com destaque de orador ——
function VideoCard({ track, label, speaking }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!track || !ref.current) return;
    track.play(ref.current);
    return () => { try { track.stop(); } catch {} };
  }, [track]);

  return (
    <div className="rounded-2xl overflow-hidden relative bg-black" style={{
      width: "100%",
      aspectRatio: "16 / 9",
      minHeight: 180,
      outline: speaking ? "3px solid #22c55e" : "none",
      transition: "outline 160ms ease",
    }}>
      <div ref={ref} style={{ width: "100%,", height: "100%" }} />
      {label && (
        <div style={{ position: "absolute", left: 12, bottom: 12, padding: "4px 8px", background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 8, fontSize: 12 }}>{label}</div>
      )}
    </div>
  );
}

// —— Lobby ——
function LobbyControls({ appId, setAppId, channel, setChannel, token, setToken, devices, selectedMicId, setSelectedMicId, selectedCamId, setSelectedCamId, onJoin }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Lobby</h2>
      <label style={{ fontWeight: 600 }}>App ID</label>
      <input value={appId} onChange={e => setAppId(e.target.value)} placeholder="SEU_APP_ID" style={inputStyle} />

      <label style={{ fontWeight: 600, marginTop: 8 }}>Canal</label>
      <input value={channel} onChange={e => setChannel(e.target.value)} placeholder="sala-teste" style={inputStyle} />

      <label style={{ fontWeight: 600, marginTop: 8 }}>Token (backend em produção)</label>
      <input value={token} onChange={e => setToken(e.target.value)} placeholder="Cole o token aqui" style={inputStyle} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
        <div>
          <label style={{ fontWeight: 600 }}>Microfone</label>
          <select value={selectedMicId} onChange={(e) => setSelectedMicId(e.target.value)} style={inputStyle}>
            {devices.mics.map((m) => (<option key={m.deviceId} value={m.deviceId}>{m.label || `Mic ${m.deviceId.slice(0,6)}`}</option>))}
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 600 }}>Câmera</label>
          <select value={selectedCamId} onChange={(e) => setSelectedCamId(e.target.value)} style={inputStyle}>
            {devices.cams.map((c) => (<option key={c.deviceId} value={c.deviceId}>{c.label || `Cam ${c.deviceId.slice(0,6)}`}</option>))}
          </select>
        </div>
      </div>

      <button onClick={onJoin} style={primaryButtonStyle}>Entrar</button>
    </div>
  );
}

// —— In-Call ——
function InCallControls({ micOn, camOn, toggleMic, toggleCam, isScreenSharing, startScreenShare, stopScreenShare, onLeave, netQ, statusMsg }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Em chamada</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <button onClick={toggleMic} style={secondaryButtonStyle}>{micOn ? "Mutar mic" : "Ativar mic"}</button>
        <button onClick={toggleCam} style={secondaryButtonStyle} disabled={isScreenSharing}>{camOn ? "Ocultar cam" : "Mostrar cam"}</button>
        {!isScreenSharing ? (
          <button onClick={startScreenShare} style={secondaryButtonStyle}>Compartilhar tela</button>
        ) : (
          <button onClick={stopScreenShare} style={secondaryButtonStyle}>Parar compartilhamento</button>
        )}
        <button onClick={onLeave} style={dangerButtonStyle}>Sair</button>
      </div>

      <div style={{ marginTop: 10, color: "#374151", fontSize: 14 }}>
        Qualidade: uplink {netQ.uplink}/5 • downlink {netQ.downlink}/5
      </div>
      <div style={{ marginTop: 4, color: "#374151", fontSize: 13 }}>{statusMsg}</div>
    </div>
  );
}

// ========== Estilos utilitários =============================================
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  outline: "none",
  fontSize: 14,
};

const primaryButtonStyle = {
  background: "#16a34a",
  color: "white",
  padding: "10px 14px",
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
  marginTop: 8,
  fontWeight: 700,
};

const secondaryButtonStyle = {
  background: "#f3f4f6",
  color: "#111827",
  padding: "10px 14px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  cursor: "pointer",
  marginTop: 8,
  fontWeight: 600,
};

const dangerButtonStyle = {
  background: "#ef4444",
  color: "white",
  padding: "10px 14px",
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
  marginTop: 8,
  fontWeight: 700,
};
